// src/index.js
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";

import { ProfileProvider } from "./context/ProfileContext";
import { AppAccessProvider } from "./context/AppAccessContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TenantProvider, useTenant } from "./context/TenantContext";
import { supabase } from "services/supabaseClient";

/** ─────────────────────────────────────────────────────────────────
 *  Global auth listener: only hard-redirect for inactivity logout.
 *  Manual / normal logout should be handled by React routing inside
 *  the app, not by window.location.replace("/"), to avoid remount
 *  blips and duplicate navigation.
 *  ──────────────────────────────────────────────────────────────── */
(function setupGlobalAuthListener() {
  try {
    // Clean up any previous listener (HMR / double-mount safe)
    if (window.__authSub && typeof window.__authSub.unsubscribe === "function") {
      window.__authSub.unsubscribe();
      window.__authSub = null;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event /*, session */) => {
      const wasManual = localStorage.getItem("compass.manualLogout") === "1";
      const wasInactivity = localStorage.getItem("compass.inactivityLogout") === "1";

      if (event === "SIGNED_OUT") {
        // Always stop any future accidental restore
        try {
          sessionStorage.removeItem("debtstudio.session");
          localStorage.removeItem("debtstudio.lastSessionBackup");
        } catch (e) {
          console.warn("[AuthGate] Cleanup error (ignored):", e);
        }

        if (wasInactivity && !wasManual) {

          // leave the inactivity flag in place so SessionExpired flow can inspect it
          if (window.location.pathname !== "/session-expired") {
            window.location.replace("/session-expired");
          }

          localStorage.removeItem("compass.manualLogout");
          return;
        }

        // Manual / normal logout:
        // do not hard redirect here.
        // Let ProfileContext / React Router handle navigation so the app
        // does not fully remount and flash during logout.
        localStorage.removeItem("compass.inactivityLogout");
        localStorage.removeItem("compass.manualLogout");
        return;
      }

      if (event === "SIGNED_IN") {
        // On fresh login, clear any leftover flags so they can't leak into future flow
        localStorage.removeItem("compass.inactivityLogout");
        localStorage.removeItem("compass.manualLogout");
      }
    });

    // Keep a handle for HMR cleanup
    window.__authSub = sub?.subscription || sub;
  } catch (err) {
    console.warn("[AuthGate] Failed to install global auth listener:", err);
  }
})();

/** Simple TenantGate so the JSX below matches your existing tree */
function TenantGate({ children }) {
  try {
    const tenant = useTenant?.();
    if (tenant && tenant.loading) return null;
  } catch {
    // if useTenant isn't providing loading, just render
  }
  return children;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <TenantProvider>
    <TenantGate>
      <BrowserRouter>
        <ThemeProvider>
          <ProfileProvider>
            <AppAccessProvider>
              <App />
            </AppAccessProvider>
          </ProfileProvider>
        </ThemeProvider>
      </BrowserRouter>
    </TenantGate>
  </TenantProvider>
);